"use client";

import { useState } from "react";
import { Button } from "@astryxdesign/core/Button";
import { Popover } from "@astryxdesign/core/Popover";
import { VStack } from "@astryxdesign/core/VStack";
import { Text } from "@astryxdesign/core/Text";
import { Icon } from "@astryxdesign/core/Icon";
import {
  COMPANY_PHONE_DISPLAY,
  COMPANY_PHONE_E164,
} from "@/lib/brand/organization";
import { useOrganizationProfile } from "@/components/organization/organization-profile-context";
import { researchIcons } from "@/lib/icons/research-icons";
import { useLeadRequest } from "@/components/landing/lead-request-context";

type Props = {
  placement?: "above" | "below";
  className?: string;
  showNumber?: boolean;
  /** Иконка в шапке; номер только в popover. */
  compact?: boolean;
};

export function LandingPhoneContact({
  placement = "below",
  className,
  showNumber = true,
  compact = false,
}: Props) {
  const { openLeadRequest } = useLeadRequest();
  const [open, setOpen] = useState(false);
  const org = useOrganizationProfile();
  const phoneDisplay = org.phoneDisplay || COMPANY_PHONE_DISPLAY;
  const phoneE164 = org.phoneE164 || COMPANY_PHONE_E164;

  function requestCallback() {
    setOpen(false);
    openLeadRequest({
      formCaseKey: "callback-request",
      title: "Заказать звонок",
      subtitle: "Оставьте номер — перезвоним в рабочее время.",
    });
  }

  return (
    <Popover
      label="Связаться по телефону"
      placement={placement}
      alignment="end"
      width={280}
      isOpen={open}
      onOpenChange={setOpen}
      content={
        <VStack gap={3} width="100%" align="stretch">
          <VStack gap={1} align="start">
            <Text weight="semibold">{phoneDisplay}</Text>
            <Text type="supporting" color="secondary">
              Позвоните сами или оставьте заявку — перезвоним.
            </Text>
          </VStack>
          <VStack gap={2} width="100%" align="stretch">
            <a href={`tel:${phoneE164}`} onClick={() => setOpen(false)}>
              <Button
                label="Позвонить"
                variant="primary"
                size="sm"
                className="w-full"
                icon={
                  <Icon icon={researchIcons.phone} size="sm" color="inherit" />
                }
              />
            </a>
            <Button
              label="Заказать звонок"
              variant="secondary"
              size="sm"
              className="w-full"
              onClick={requestCallback}
            />
          </VStack>
        </VStack>
      }
    >
      {(triggerProps) => (
        <button
          {...triggerProps}
          type="button"
          className={
            className ??
            (compact
              ? "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-transparent text-primary transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              : "inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-transparent px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2")
          }
          aria-label={`Телефон ${phoneDisplay}`}
        >
          <Icon icon={researchIcons.phone} size="sm" color="secondary" />
          {!compact && showNumber ? (
            <>
              <span className="hidden min-[420px]:inline">{phoneDisplay}</span>
              <span className="inline min-[420px]:hidden">Позвонить</span>
            </>
          ) : (
            <span className="sr-only">{phoneDisplay}</span>
          )}
        </button>
      )}
    </Popover>
  );
}
